export const SITE = {
  name: "Amaya Pro",
  domain: "https://amaya.energy",
  tagline: "Intelligent lighting control for smarter buildings.",
  description:
    "Amaya Pro connects lighting, sensors and controls through a secure Bluetooth Mesh network — automated lighting, scheduling, dimming, energy monitoring and remote management for commercial and industrial buildings.",
  phone: "(214) 571-6374",
  supportPhone: "(302) 391-6444",
  email: "support@amayapro.com",
  address: "1111 N DuPage Ave, Lombard, IL 60148",
};

export const TRUST_ITEMS = [
  "Bluetooth Mesh",
  "Network Lighting Control",
  "DLC QPL — Networked Lighting Controls",
  "PSA Level 3 Security",
  "10-Year In-House Warranty",
  "Energy Monitoring",
  "Remote Management",
];

export const DLC = [
  { scope: "Indoor Products", productId: "N-Q0TFLJ", status: "Listed", qualified: "2025-12-10", qpl: "Networked Lighting Controls" },
  { scope: "Outdoor Products", productId: "N-KGDT1F", status: "Listed", qualified: "2026-01-28", qpl: "Networked Lighting Controls" },
];

export const CAPABILITIES = [
  { name: "Occupancy / Vacancy", desc: "Lighting responds automatically to occupancy and motion." },
  { name: "Daylight Harvesting", desc: "Artificial light adjusts to available natural light, maintaining targets while cutting waste." },
  { name: "Scheduling", desc: "Clock-dial scheduling programs lighting around time, work hours and overrides." },
  { name: "Bi-Level Dimming", desc: "Switch between full and reduced output based on occupancy." },
  { name: "Continuous Dimming", desc: "Smooth 0–10V dimming across the control line." },
  { name: "High-End Trim", desc: "Cap maximum output to lock in savings and extend fixture life." },
  { name: "Energy Monitoring", desc: "Real-time and historical lighting consumption data." },
  { name: "Remote Access", desc: "Control and manage the system through supported mobile and web tools." },
  { name: "Zoning", desc: "Group luminaires into zones and control them together." },
  { name: "Individual Luminaire Addressability", desc: "Address and control each luminaire on the network." },
  { name: "Real-Time Monitoring", desc: "Live system and device status with automatic health reporting." },
  { name: "Alerts", desc: "Alert notifications keep facility teams informed." },
  { name: "Asset Management", desc: "Track and manage lighting assets across the site." },
  { name: "User Permissions", desc: "Controlled access with user-level permissions." },
  { name: "Firmware Updates", desc: "Keep devices current with firmware update support." },
  { name: "Fail-Safe Operation", desc: "Designed to keep the site running without downtime." },
  { name: "Secure Connectivity", desc: "PSA Level 3 security across Amaya Pro devices." },
];

export const WHY = [
  { k: "Smarter", desc: "Automated lighting driven by occupancy, daylight and schedules." },
  { k: "Connected", desc: "Bluetooth Mesh links luminaires, sensors and controls into one network." },
  { k: "Scalable", desc: "Wireless architecture built for large facilities and multi-zone sites." },
  { k: "Efficient", desc: "Energy monitoring and intelligent strategies optimize operation." },
  { k: "Manageable", desc: "Mobile, desktop and online portal tools for control and programming." },
  { k: "Secure", desc: "PSA Level 3 security specified across Amaya Pro devices." },
];

export const LAYERS = [
  { id: "SENSE", items: ["PIR", "OcuSense", "PIR / Ultrasonic", "Photocell"] },
  { id: "CONTROL", items: ["BLE Controls", "Nodes", "Power Pack Nodes", "Wall Switches", "Wall Outlet"] },
  { id: "CONNECT", items: ["Bluetooth Mesh"] },
  { id: "MANAGE", items: ["Mobile App", "Desktop / Web", "Online Portal"] },
  { id: "OPTIMIZE", items: ["Scheduling", "Dimming", "Daylight Harvesting", "Energy Monitoring", "Remote Management"] },
];

export const COMMISSIONING = [
  { n: "01", t: "Plan", d: "Plan locations and control requirements with the online portal." },
  { n: "02", t: "Configure", d: "Program controls, zones and schedules before or after install." },
  { n: "03", t: "Commission", d: "Commission with the supported commissioning remote and app." },
  { n: "04", t: "Control", d: "Manage lighting and user preferences from app, desktop or wall controls." },
  { n: "05", t: "Monitor", d: "Track device health status and real-time energy data." },
  { n: "06", t: "Optimize", d: "Use energy information and system controls to improve operation." },
];

export const SOLUTIONS = [
  {
    id: "energy-optimization", title: "Energy Optimization",
    desc: "Cut lighting energy waste with layered, automatic strategies that run around the clock.",
    points: ["Daylight harvesting", "Occupancy & vacancy control", "Automatic scheduling", "Bi-level & continuous dimming", "Energy monitoring and reports"],
  },
  {
    id: "smart-lighting-automation", title: "Smart Lighting Automation",
    desc: "Lighting that responds to people, time and daylight — without manual intervention.",
    points: ["Occupancy and vacancy sensing", "Time-based scheduling", "Personal control via app and remotes", "Scene recall on self-powered wall switches"],
  },
  {
    id: "remote-management", title: "Remote Management",
    desc: "Commission, monitor and adjust the system from anywhere through the Amaya Pro web and app tools.",
    points: ["Remote control and access", "Online portal for pre/post programming", "Automatic health status reporting", "Monitoring and alert notifications"],
  },
  {
    id: "large-facility-control", title: "Large Facility Control",
    desc: "A wireless mesh architecture that scales across floors, buildings and campuses.",
    points: ["Bluetooth Mesh many-to-many networking", "Long Range Extenders up to 328 ft", "Power Pack Nodes with 20A relays", "Zoning and individual luminaire addressability"],
  },
  {
    id: "building-performance", title: "Building Performance",
    desc: "Turn lighting into a managed, measured building system.",
    points: ["Real-time and historical energy data", "Real-time device monitoring", "Asset management", "User permissions and firmware updates"],
  },
];

export const APPLICATION_CARDS = [
  {
    id: "office", title: "Office", app: "Office",
    challenge: "Lights must respond to occupancy, schedules and daylight while still allowing personal control.",
    answer: "PIR and OcuSense controls with Bluetooth Mesh, scheduling, dimming and app-based personal control.",
    products: ["06901", "06936", "06937", "06951", "06952", "06953", "06954"],
  },
  {
    id: "commercial", title: "Commercial", app: "Office",
    challenge: "Multi-zone commercial floors need centralized control without rewiring the building.",
    answer: "Wireless Bluetooth Mesh controls, wall switches and nodes commissioned and managed from one portal.",
    products: ["06902", "06903", "06936", "06951", "06954"],
  },
  {
    id: "industrial", title: "Industrial", app: "High-Bay",
    challenge: "Large industrial spaces burn energy lighting empty aisles and unoccupied zones.",
    answer: "High-bay PIR/OcuSense controls with bi-level dimming, high-end trim and energy monitoring.",
    products: ["06905", "06906", "06908", "06930", "06932"],
  },
  {
    id: "high-bay", title: "High-Bay", app: "High-Bay",
    challenge: "Mounting heights up to 50 ft demand sensors and controls built for long-range detection.",
    answer: "High-bay ready controls up to 40–50 ft mounting, IP65 options and 0–10V dimming.",
    products: ["06907", "06910", "06915", "06931", "06933"],
  },
  {
    id: "low-bay", title: "Low-Bay", app: "Low-Bay",
    challenge: "Low-bay areas need wide 360° coverage at moderate mounting heights.",
    answer: "Low-bay ready PIR and OcuSense controls at 13–15 ft with daylight harvesting.",
    products: ["06901", "06902", "06904", "06914", "06936"],
  },
  {
    id: "outdoor", title: "Outdoor", app: "Outdoor",
    challenge: "Exterior and perimeter lighting must follow daylight and occupancy — not a timer alone.",
    answer: "IP65 outdoor PIR controls with photocell and scheduling, mounted up to 40 ft.",
    products: ["06921", "06922", "06970", "06971"],
  },
  {
    id: "residential", title: "Residential", app: "Office",
    challenge: "Residential facilities need simple, reliable control with minimal maintenance.",
    answer: "Self-powered wall switches and compact BLE controls with app-based management.",
    products: ["06952", "06953", "06936", "06951"],
  },
];

export const FAQS = [
  { q: "What is Network Lighting Control (NLC)?", a: "NLC systems connect luminaires, sensors and controls into a network, enabling automated operation, centralized management, energy optimization, code compliance and real-time monitoring across residential, commercial and industrial facilities." },
  { q: "What is Bluetooth Mesh?", a: "Bluetooth Mesh is a secure, low-power wireless protocol enabling many-to-many communication between lighting devices — providing scalable, reliable and resilient control across large facilities." },
  { q: "What is occupancy sensing?", a: "Amaya Pro PIR, OcuSense and PIR/Ultrasonic sensors detect motion and occupancy so lighting responds automatically — on when spaces are used, reduced or off when they are not." },
  { q: "What is daylight harvesting?", a: "Daylight harvesting automatically adjusts artificial lighting based on available natural light, maintaining illumination targets while reducing energy consumption." },
  { q: "What is bi-level dimming?", a: "Bi-level dimming switches lighting between full and reduced levels based on occupancy, delivering energy savings and extending fixture life." },
  { q: "What is high-end trim?", a: "High-end trim caps the maximum light output of luminaires, locking in additional energy savings without affecting required light levels." },
  { q: "How does scheduling work?", a: "Clock-dial scheduling programs lighting behavior by time — supporting work hours, overrides and energy-efficient facility operation." },
  { q: "Does Amaya Pro monitor energy?", a: "Yes. Energy monitoring provides real-time and historical lighting consumption data, enabling compliance verification, performance analysis and continuous optimization." },
  { q: "What is the wireless communication range?", a: "Most Amaya Pro controls communicate up to 150 ft. Extended-range products reach 328 ft (06933) and Long Range Extenders provide up to 328 ft (06970) or 200 ft (06971) of Bluetooth range. The BLE Wall Outlet reaches 65 ft." },
  { q: "What warranty does Amaya Pro carry?", a: "Amaya Pro includes a 10-year in-house warranty, backed by quick support and replacements to keep sites running without downtime." },
  { q: "What software does the system include?", a: "The Amaya Pro system includes a mobile app, desktop-friendly tools and a robust online portal for pre-planning, pre & post programming, location controls and energy data. Controls can also be programmed via a commissioning remote, and a handheld remote is available for on-demand user preference." },
  { q: "Is the system certified?", a: "Amaya Pro Networked Lighting Controls are listed on the DesignLights Consortium QPL — Indoor (Product ID N-Q0TFLJ) and Outdoor (Product ID N-KGDT1F). The 06934 Power Pack Node is UL Listed (Category PAZX) and UL 2043 Plenum Rated." },
];

export const NAV = [
  { to: "/products", label: "Products" },
  { to: "/technology", label: "Technology" },
  { to: "/solutions", label: "Solutions" },
  { to: "/applications", label: "Applications" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "About" },
];
